import { useState } from 'react';
import {
  TOPICS,
  eyebrowStyle, h1Style, subtitleStyle, sectionLabelStyle,
  inputStyle, primaryBtnStyle, backBtnStyle, cardStyle,
} from './tokens';
import SimilarQuestionsModal from './SimilarQuestionsModal';
import {
  findSimilarQuestions, insertQuestion, uploadAttachment, humanizeError,
} from '../../lib/communityService';

/**
 * AskQuestionFlow — collect topic/title/description/attachment/identity,
 * check for similar questions BEFORE final submit, then post to Supabase.
 */
export default function AskQuestionFlow({ userId, onClose, onOpenExisting, onPosted }) {
  const [topic, setTopic] = useState(null);
  const [title, setTitle] = useState('');
  const [desc, setDesc]   = useState('');
  const [file, setFile]   = useState(null);
  const [identity, setIdentity] = useState('anonymous');
  const [similar, setSimilar]   = useState(null);   // null | array
  const [checking, setChecking] = useState(false);
  const [posting, setPosting]   = useState(false);
  const [posted, setPosted]     = useState(false);
  const [error, setError]       = useState(null);

  const canPost = !!topic && title.trim().length >= 8 && desc.trim().length >= 20;

  const runSimilarCheckThenMaybePost = async () => {
    if (!canPost || checking || posting) return;
    setError(null);
    setChecking(true);
    try {
      const rows = await findSimilarQuestions({ topic, title, description: desc, limit: 3 });
      if (rows && rows.length > 0) {
        setSimilar(rows);   // opens modal
      } else {
        await doPost();
      }
    } finally {
      setChecking(false);
    }
  };

  const doPost = async () => {
    if (!userId) {
      setError('You need to be signed in to post.');
      return;
    }
    setPosting(true);
    setError(null);
    try {
      let attachmentPath = null;
      if (file) attachmentPath = await uploadAttachment(userId, file);
      const res = await insertQuestion({
        userId,
        topic,
        title: title.trim(),
        description: desc.trim(),
        anonymous: identity === 'anonymous',
        attachmentPath,
      });
      if (res.error) {
        setError(humanizeError(res.error));
      } else {
        setPosted(true);
        onPosted?.();
      }
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setPosting(false);
      setSimilar(null);
    }
  };

  if (posted) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }} data-testid="ask-posted-confirm">
        <div style={{ fontSize: 44, marginBottom: 12 }}>✓</div>
        <h2 style={{ fontFamily: '"Fraunces", serif', fontWeight: 400, fontSize: 22, margin: 0 }}>Question shared</h2>
        <p style={subtitleStyle}>The community will begin responding soon.</p>
        <button onClick={onClose} data-testid="ask-return-home" style={primaryBtnStyle}>Back to Community</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 16px 120px' }} data-testid="ask-question-flow">
      <button onClick={onClose} style={backBtnStyle} data-testid="ask-close-btn">← Back</button>

      <div style={{ ...eyebrowStyle, marginTop: 12 }}>ASK · A QUESTION</div>
      <h1 style={h1Style}>Share what you&rsquo;re wondering.</h1>
      <p style={subtitleStyle}>Answers come from experiences and evidence.</p>

      {/* Topic */}
      <div style={sectionLabelStyle}>Choose a topic</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
        {TOPICS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTopic(t.key)}
            data-testid={`ask-topic-${t.key}`}
            style={{
              padding: '10px 6px', borderRadius: 12, cursor: 'pointer',
              background: topic === t.key ? 'rgba(109,94,245,.10)' : '#FFFFFF',
              border: topic === t.key ? '1px solid rgba(109,94,245,.30)' : '1px solid rgba(0,0,0,.06)',
              color: '#232323', fontSize: 12, fontWeight: topic === t.key ? 600 : 500,
              fontFamily: 'inherit',
            }}
          >
            <div style={{ fontSize: 16, marginBottom: 4 }}>{t.icon}</div>
            {t.label}
          </button>
        ))}
      </div>

      {/* Title */}
      <div style={sectionLabelStyle}>Question title</div>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Why is my period 50 days late?"
        data-testid="ask-title-input"
        style={inputStyle}
      />
      {title.length > 4 && (
        <div data-testid="ask-similar-placeholder" style={{
          padding: 12, marginTop: 8, borderRadius: 12,
          background: 'rgba(94,79,127,.04)', border: '1px solid rgba(94,79,127,.10)',
          fontSize: 12, color: '#6F6A66', fontStyle: 'italic',
        }}>
          We&rsquo;ll check for similar community questions before posting.
        </div>
      )}

      {/* Description */}
      <div style={{ ...sectionLabelStyle, marginTop: 20 }}>Describe what&rsquo;s happening</div>
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder="Share the timeline, symptoms, tests you&rsquo;ve done, anything that feels relevant."
        data-testid="ask-desc-input"
        style={{ ...inputStyle, minHeight: 110, resize: 'vertical' }}
      />

      {/* Attach */}
      <div style={{ ...sectionLabelStyle, marginTop: 20 }}>Optional · attach a report or image</div>
      <label
        data-testid="ask-attach-label"
        style={{ ...cardStyle, padding: 16, textAlign: 'center', color: '#8A847C', fontSize: 13, cursor: 'pointer', display: 'block' }}
      >
        <input
          type="file"
          accept="image/*,application/pdf"
          data-testid="ask-attach-input"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ display: 'none' }}
        />
        {file ? file.name : 'Upload from your device'}
      </label>

      {/* Identity */}
      <div style={{ ...sectionLabelStyle, marginTop: 20 }}>Post as</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { key: 'me', label: 'Post as Me' },
          { key: 'anonymous', label: 'Post Anonymously' },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setIdentity(opt.key)}
            data-testid={`ask-identity-${opt.key}`}
            style={{
              flex: 1, padding: '12px 10px', borderRadius: 12, cursor: 'pointer',
              background: identity === opt.key ? 'rgba(109,94,245,.10)' : '#FFFFFF',
              border: identity === opt.key ? '1px solid rgba(109,94,245,.30)' : '1px solid rgba(0,0,0,.06)',
              color: '#232323', fontSize: 13, fontWeight: identity === opt.key ? 600 : 500,
              fontFamily: 'inherit',
            }}
          >
            {identity === opt.key ? '● ' : '○ '}{opt.label}
          </button>
        ))}
      </div>

      {/* Review + Post */}
      <button
        onClick={runSimilarCheckThenMaybePost}
        disabled={!canPost || checking || posting}
        data-testid="ask-post-btn"
        style={{
          ...primaryBtnStyle, marginTop: 24,
          opacity: (canPost && !checking && !posting) ? 1 : 0.4,
          cursor: (canPost && !checking && !posting) ? 'pointer' : 'not-allowed',
        }}
      >
        {checking ? 'Checking for similar questions…' : posting ? 'Posting…' : 'Post Question'}
      </button>
      {!canPost && (
        <div style={{ fontSize: 11, color: '#8A847C', textAlign: 'center', marginTop: 8 }}>
          Choose a topic and complete the title and description to post.
        </div>
      )}
      {error && (
        <div data-testid="ask-error" style={{ fontSize: 12, color: '#C67A69', textAlign: 'center', marginTop: 10 }}>
          {error}
        </div>
      )}

      {similar && similar.length > 0 && (
        <SimilarQuestionsModal
          similar={similar}
          posting={posting}
          onDismiss={() => setSimilar(null)}
          onPostAnyway={doPost}
          onOpen={(row) => { setSimilar(null); onOpenExisting?.(row); }}
        />
      )}
    </div>
  );
}
